import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Flame,
  LogOut,
  BookOpen,
  Play,
  Trophy,
  User,
  Coins,
} from "lucide-react";
import { auth, db } from "../lib/supabase";
import { LEVELS, ACHIEVEMENTS } from "../data/gameData";
import useGameStore from "../lib/gameStore";
import Avatar from "../components/Avatar";
import XPBar from "../components/XPBar";
import LevelCard from "../components/LevelCard";
import AchievementBadge from "../components/AchievementBadge";
import CoinsDisplay from "../components/CoinsDisplay";
import Footer from "../components/Footer";
import { ACHIEVEMENT_DEFINITIONS } from "../utils/achievements";
import { toast } from "sonner";

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    profile: userProfile,
    setProfile,
    setLevels,
    levels,
    setAchievements,
    setUserAchievements,
    achievements,
    userAchievements,
    logout,
  } = useGameStore();
  const [loading, setLoading] = useState(true);
  const [showUserPopup, setShowUserPopup] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const [authUser, setAuthUser] = useState(null);

  const loadData = async () => {
    try {
      // Get current user
      const { user, error: userError } = await auth.getUser();

      if (userError || !user) {
        throw new Error('Not authenticated');
      }

      // Store auth user for profile display
      setAuthUser(user);

      // Load profile from Supabase
      let { data: profileData, error: profileError } = await db.profile.get(user.id);

      // If profile doesn't exist, create it
      if (profileError || !profileData) {
        const userName = user.user_metadata?.name || 'Automator';
        const newProfile = {
          user_id: user.id,
          avatar_type: 'ROBOT',
          avatar_color: '#FF6B6B',
          avatar_name: userName,
          display_name: userName,
          total_xp: 0,
          current_level: 1,
          coins: 0,
          streak_days: 0,
          last_login: new Date().toISOString()
        };

        const { data: createdProfile, error: createError } = await db.profile.create(newProfile);

        if (createError) {
          throw createError;
        }

        profileData = createdProfile;
      }

      // Load user progress from Supabase
      const { data: progressData, error: progressError } = await db.progress.getAll(user.id);

      if (progressError) {
        throw progressError;
      }

      // Load user achievements from Supabase
      const { data: userAchievementsData, error: achievementsError } = await db.achievements.getAll(user.id);

      if (achievementsError) {
        throw achievementsError;
      }

      // Combine local LEVELS data with user progress
      const levelsWithProgress = LEVELS.map(level => {
        const userProgress = progressData?.find(p => p.level_id === level.id);
        const isLocked = level.required_xp > (profileData?.total_xp || 0);

        return {
          ...level,
          is_completed: userProgress?.is_completed || false,
          is_locked: isLocked,
          attempt_count: userProgress?.attempt_count || 0,
          best_score: userProgress?.best_score || 0
        };
      });

      // Set all data
      setProfile(profileData);
      setLevels(levelsWithProgress);
      setAchievements(ACHIEVEMENTS);
      setUserAchievements(userAchievementsData || []);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error("Error al cargar datos");
      logout();
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sesión cerrada");
  };

  const unlockedAchievements = achievements.filter((a) =>
    userAchievements.some((ua) => ua.achievement_id === a.achievement_id)
  );

  const nextLevel = levels.find((l) => !l.is_completed && !l.is_locked);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-heading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="dashboard">
      {/* Header */}
      <header className="glass border-b border-white/10 sticky top-0 z-50 backdrop-blur-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 relative">
              {/* Avatar - Clickable on mobile */}
              <button
                onClick={() => setShowUserPopup(!showUserPopup)}
                className="md:cursor-default"
              >
                <Avatar
                  type={userProfile?.avatar_type}
                  color={userProfile?.avatar_color}
                  size="sm"
                  customImage={userProfile?.avatar_url}
                />
              </button>

              {/* User info - Hidden on mobile, visible on desktop */}
              <div className="hidden md:block">
                <h2 className="text-lg font-heading font-bold text-text-primary">
                  {userProfile?.display_name || authUser?.user_metadata?.name || userProfile?.avatar_name || 'Usuario'}
                </h2>
                <p className="text-sm text-text-secondary">
                  {authUser?.email}
                </p>
              </div>

              {/* Mobile popup */}
              <AnimatePresence>
                {showUserPopup && (
                  <>
                    {/* Overlay to close popup */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="md:hidden fixed inset-0 z-40"
                      onClick={() => setShowUserPopup(false)}
                    />
                    {/* Popup content */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="md:hidden absolute top-12 left-0 glass p-4 rounded-lg shadow-xl border border-primary/30 z-50 min-w-[200px]"
                    >
                      <div>
                        <h3 className="text-sm font-heading font-bold text-text-primary mb-1">
                          {userProfile?.display_name || authUser?.user_metadata?.name || userProfile?.avatar_name || 'Usuario'}
                        </h3>
                        <p className="text-xs text-text-secondary">
                          {authUser?.email}
                        </p>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <div className="flex items-center gap-4">
              <div
                className="flex items-center gap-2"
                data-testid="streak-counter"
              >
                <CoinsDisplay coins={userProfile?.coins || 0} />
                <Flame
                  className="w-6 h-6 text-orange-500"
                  fill="currentColor"
                />
                <span className="text-xl font-heading font-bold text-text-primary">
                  {userProfile?.streak_days || 0}
                </span>
              </div>
              <button
                onClick={() => navigate("/profile")}
                className="text-text-secondary hover:text-primary transition-colors"
                data-testid="profile-btn"
                title="Mi Perfil"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </button>
              <button
                onClick={handleLogout}
                className="text-text-secondary hover:text-primary transition-colors"
                data-testid="logout-btn"
                title="Cerrar Sesión"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 flex-1">
        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {/* Stats Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass p-6 rounded-card lg:col-span-1"
          >
            <div className="flex justify-center mb-4">
              <Avatar
                type={userProfile?.avatar_type}
                color={userProfile?.avatar_color}
                size="lg"
                customImage={userProfile?.avatar_url}
              />
            </div>
            <div className="mb-6">
              <XPBar
                currentXP={userProfile?.total_xp || 0}
                level={userProfile?.current_level || 1}
                completedLevels={levels.filter((l) => l.is_completed).length}
              />
            </div>
            <div className="space-y-3">
              <StatItem label="Total XP" value={userProfile?.total_xp || 0} />
              <StatItem label="Monedas" value={userProfile?.coins || 0} />
              <StatItem
                label="Niveles Completados"
                value={levels.filter((l) => l.is_completed).length}
              />
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass p-6 rounded-card lg:col-span-3"
          >
            <h2 className="text-2xl font-heading font-bold text-text-primary mb-4">
              Acciones Rápidas
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <QuickActionCard
                icon={<Play className="w-8 h-8" />}
                title="Continuar Aprendiendo"
                description={
                  nextLevel
                    ? `Nivel ${nextLevel.id}: ${nextLevel.title}`
                    : "Todos los niveles completados"
                }
                onClick={() => nextLevel && navigate(`/level/${nextLevel.id}`)}
                disabled={!nextLevel}
                testId="continue-learning-btn"
              />
              <QuickActionCard
                icon={<BookOpen className="w-8 h-8" />}
                title="Glosario"
                description="Explora conceptos de n8n"
                onClick={() => navigate("/concepts")}
                testId="glossary-btn"
              />
              <QuickActionCard
                icon={<Trophy className="w-8 h-8" />}
                title="Sandbox"
                description="Experimenta libremente"
                onClick={() => navigate("/sandbox")}
                testId="sandbox-btn"
              />
            </div>
          </motion.div>
        </div>

        {/* Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass p-6 rounded-card mb-8"
        >
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-4">
            Logros Milestone
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {ACHIEVEMENT_DEFINITIONS.map((achievement, index) => {
              const isUnlocked = userAchievements.some(
                (ua) => ua.achievement_id === achievement.id
              );
              const completedCount = levels.filter(
                (l) => l.is_completed
              ).length;

              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * index }}
                  className={`glass p-6 rounded-card border-2 transition-all ${
                    isUnlocked
                      ? "border-primary/50 bg-primary/5"
                      : "border-slate-700"
                  }`}
                  data-testid={`achievement-${achievement.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center text-4xl ${
                        isUnlocked
                          ? "bg-gradient-to-br from-primary to-secondary"
                          : "bg-background-subtle"
                      }`}
                    >
                      {isUnlocked ? (
                        <Trophy
                          className="w-10 h-10 text-white"
                          fill="currentColor"
                        />
                      ) : (
                        "🔒"
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-heading font-bold text-text-primary mb-1">
                        {achievement.title}
                      </h3>
                      <p className="text-sm text-text-secondary mb-2">
                        {achievement.description}
                      </p>
                      {isUnlocked ? (
                        <span className="inline-flex items-center gap-1 text-xs text-success font-medium">
                          ✓ Desbloqueado
                        </span>
                      ) : (
                        <div className="text-xs text-text-muted">
                          Progreso: {completedCount}/
                          {achievement.requirement === "complete_6_levels"
                            ? 6
                            : achievement.requirement === "complete_12_levels"
                            ? 12
                            : achievement.requirement === "complete_19_levels"
                            ? 19
                            : 0}{" "}
                          niveles
                        </div>
                      )}
                      {isUnlocked && (
                        <div className="mt-2 text-xs text-warning font-bold">
                          +{achievement.coinReward} monedas
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Levels Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-3xl font-heading font-bold text-text-primary mb-6">
            Línea de Tiempo de Niveles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {levels.map((level, index) => (
              <LevelCard
                key={level.id}
                level={level}
                onClick={() => navigate(`/level/${level.id}`)}
                index={index}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

const StatItem = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-text-secondary text-sm">{label}</span>
    <span className="text-text-primary font-heading font-bold">{value}</span>
  </div>
);

const QuickActionCard = ({
  icon,
  title,
  description,
  onClick,
  disabled = false,
  testId,
}) => (
  <motion.button
    whileHover={!disabled ? { scale: 1.05, y: -2 } : {}}
    whileTap={!disabled ? { scale: 0.98 } : {}}
    onClick={onClick}
    disabled={disabled}
    className={`glass p-4 rounded-lg text-left transition-all ${
      disabled
        ? "opacity-50 cursor-not-allowed"
        : "hover:border-primary/50 cursor-pointer"
    }`}
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-2">
      <div className="text-primary">{icon}</div>
      <h3 className="font-heading font-bold text-text-primary">{title}</h3>
    </div>
    <p className="text-sm text-text-secondary">{description}</p>
  </motion.button>
);

export default Dashboard;
